import { AxiosError, AxiosResponse } from 'axios';
import { Observable, catchError, from, map, tap } from 'rxjs';

import APIAxios, { APIRoutes } from '@api/axios.api';
import SnackError from '@utils/error.utils';


import { GridSortItem } from '@mui/x-data-grid-pro';
import { setEntities } from '@ngneat/elf-entities';
import { setPage, updatePaginationData } from '@ngneat/elf-pagination';
import { Pagination } from '@store/common/pagination.model';
import { TableFilter } from '@store/common/tableFilter.model';
import { AuditOfferSupplierLightDto, AuditProposalSuppliers, AuditProposalSuppliersFilters, AuditProposalSuppliersPagination, SupplierUnitsProposalDto, UpdateAuditProposalSupplierDto } from './audit-proposal-suppliers.model';
import { auditProposalSuppliersStore } from './audit-proposal-suppliers.store';

import { AuditOperation } from '@store/audits/audits-operation/audits-operation.model';
import { getAllAuditsOfferSupProposalDataSource, getAuditProposalSuppliersDataSource, getAuditsOfferSupFromProposalSupDataSource, getAuditsProposalSupplierDetails } from './audit-proposal-suppliers.requests';

export class AuditProposalSuppliersServices {
  store = auditProposalSuppliersStore;

  resetStore = () => this.store.reset();

  setSort = (sort: GridSortItem) => this.store.update((state) => ({ ...state, sort }));
  setPagination = (name: string) => (value: number) => this.store.update((state) => ({
    ...state,
    tablePagination: {
      ...state.tablePagination,
      [name]: value,
    },
  }));

  resetFilters = () => this.store.update((state) => ({
    ...state,
    filters: { search: '', tableFilters: AuditProposalSuppliersFilters.initTableFilters() },
  }));
  setFilters = (name: string) => (value: any) => this.store.update((state) => ({
    ...state,
    filters: {
      ...state.filters,
      [name]: value,
    },
  }));

  getAuditsProposal = (auditId: string | number, isExpiredProposal?: boolean, supplier?: SupplierUnitsProposalDto, auditOp?: AuditOperation): Observable<AuditProposalSuppliersPagination> => {
    const { filters: { search, tableFilters }, sort, tablePagination } = this.store.getValue();
    const requestFilters = TableFilter.toRequestFilters(tableFilters);

    return from(APIAxios({
      ...APIRoutes.GETAuditsProposalSuppliers(auditId),
      params: {
        ...requestFilters,
        searchString: search || undefined,
        page: tablePagination.page,
        limit: tablePagination.limit,
        sortBy: sort.field,
        orderBy: sort.sort?.toLocaleUpperCase(),
        isExpiredProposal: isExpiredProposal,
        supplierOrganisationUnitId: supplier?.id,
        auditOperationId: auditOp?.id,
      },
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<AuditProposalSuppliersPagination>) => {
          return response.data;
        }),
        tap((proposalSupplierPagination) => {
          const paginationData = Pagination.toPaginationData(proposalSupplierPagination, tablePagination.page);
          return this.store.update(
            setEntities(proposalSupplierPagination.data),
            updatePaginationData(paginationData),
            setPage(paginationData.currentPage, proposalSupplierPagination.data.map((proposalSupplier) => proposalSupplier.id)),
            getAuditProposalSuppliersDataSource.setSuccess(),
          );
        }),
        getAuditProposalSuppliersDataSource.trackRequestStatus(),
      );
  };


  getAuditsProposalSupplierDetails = (auditProposalSupplierId: string | number): Observable<AuditProposalSuppliers> => {
   return from(APIAxios({
      ...APIRoutes.GETAuditsProposalSupplierDetails(auditProposalSupplierId),
    }))
      .pipe(
          catchError((err: AxiosError) => {
            throw new SnackError((err.response as any)?.data?.message, 'error');
          }),
          map((response: AxiosResponse<AuditProposalSuppliers>) => {
            return response.data;
          }),
          tap((auditsProposalSupplierDetails) => {
              this.store.update((state) => ({
                  ...state,
                  auditsProposalSupplierDetails,
              }));
              getAuditsProposalSupplierDetails.setSuccess();
          }),
          getAuditsProposalSupplierDetails.trackRequestStatus(),
        );
  };

  getAuditsOfferSupFromProposalSup = (auditProposalSupplierId: string | number): Observable<AuditOfferSupplierLightDto[]> => {
   return from(APIAxios({
      ...APIRoutes.GETAuditsOfferSupFromProposalSup(auditProposalSupplierId),
    }))
      .pipe(
          catchError((err: AxiosError) => {
            throw new SnackError((err.response as any)?.data?.message, 'error');
          }),
          map((response: AxiosResponse<AuditOfferSupplierLightDto[]>) => {
            return response.data;
          }),
          tap((allAuditsOfferSupFromProposalSup) => {
              this.store.update((state) => ({
                  ...state,
                  allAuditsOfferSupFromProposalSup,
              }));
              getAuditsOfferSupFromProposalSupDataSource.setSuccess();
          }),
          getAuditsOfferSupFromProposalSupDataSource.trackRequestStatus(),
        );
  };

    patchAuditsOfferSupFromProposalSup = (auditProposalSupplierId: number | string, data: UpdateAuditProposalSupplierDto): Observable<AuditOfferSupplierLightDto[]> => {
   return from(APIAxios({
      ...APIRoutes.PATCHAuditsOfferSupFromProposalSup(auditProposalSupplierId),
      data,
    }))
      .pipe(
          catchError((err: AxiosError) => {
            throw new SnackError((err.response as any)?.data?.message, 'error');
          }),
          map((response: AxiosResponse<AuditOfferSupplierLightDto[]>) => {
            return response.data;
          }),
          tap((allAuditsOfferSupFromProposalSup) => {
              this.store.update((state) => ({
                  ...state,
                  allAuditsOfferSupFromProposalSup,
              }));
              getAuditsOfferSupFromProposalSupDataSource.setSuccess();
          }),
          getAuditsOfferSupFromProposalSupDataSource.trackRequestStatus(),
        );
  };

  getAllAuditsSupProposal = (auditProposalSupplierId: number | string): Observable<SupplierUnitsProposalDto[]> => {
   return from(APIAxios({
      ...APIRoutes.GETAllAuditsSupProposal(auditProposalSupplierId),
    }))
      .pipe(
          catchError((err: AxiosError) => {
            throw new SnackError((err.response as any)?.data?.message, 'error');
          }),
          map((response: AxiosResponse<SupplierUnitsProposalDto[]>) => {
            return response.data;
          }),
          tap((allAuditsSupProposal) => {
              this.store.update((state) => ({
                  ...state,
                  allAuditsSupProposal,
              }));
              getAllAuditsOfferSupProposalDataSource.setSuccess();
          }),
          getAllAuditsOfferSupProposalDataSource.trackRequestStatus(),
        );
  };

   patchAuditsSupSentAT = (auditProposalSupplierId: number | string): Observable<AuditProposalSuppliers> => {
   return from(APIAxios({
      ...APIRoutes.PATCHAuditsSupSentAT(auditProposalSupplierId),
    }))
      .pipe(
          catchError((err: AxiosError) => {
            throw new SnackError((err.response as any)?.data?.message, 'error');
          }),
          map((response: AxiosResponse<AuditProposalSuppliers>) => {
            return response.data;
          }),
          tap((AuditProposalSuppliersData) => {
              this.store.update((state) => ({
                  ...state,
                  auditsProposalSupplierDetails : {
                    ...state.auditsProposalSupplierDetails as AuditProposalSuppliers,
                    sentAt: AuditProposalSuppliersData.sentAt
                  },
              }));
              getAuditsOfferSupFromProposalSupDataSource.setSuccess();
          }),
          getAuditsOfferSupFromProposalSupDataSource.trackRequestStatus(),
        );
  };

    patchAuditsSupEmail = (auditsProposalDetailsId: string | number, email: string) => {
    return from(APIAxios({
      ...APIRoutes.PATCHAuditpatchAuditsSupEmail(auditsProposalDetailsId),
      data: { email: email },
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<AuditProposalSuppliers>) => {
          return response.data;
        }),
        tap((proposalResponse) => {
            this.store.update((state) => ({
                ...state,
                auditsProposalSupplierDetails: {
                    ...state.auditsProposalSupplierDetails as AuditProposalSuppliers,
                    email: proposalResponse.email,
                }
                ,
            }));
        }),
      );
  };

}

export const auditProposalSuppliersServices = new AuditProposalSuppliersServices();
